import { Box, Stack, type StackProps, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'

export interface RootProps extends StackProps {
  corners: 'rounded' | 'square'
  size: 'small' | 'medium'
  margin: 'normal' | 'none'
  fullWidth?: boolean
}

const cornersMap = {
  rounded: '5px',
  square: 0,
} as const

const rootContainerName = 'root'

export const Root = styled(Box, {
  shouldForwardProp: youShallNotPass('corners', 'size', 'margin', 'fullWidth'),
})<RootProps>(({ theme, corners, margin, fullWidth }) => ({
  borderRadius: cornersMap[corners],
  ...(margin === 'normal' && { margin: theme.spacing(1, 0) }),
  backgroundColor: theme.tokens.ThemeColorCommonBackground,
  color: theme.tokens.ThemeColorCommonOnSurfaceSecondary,
  ...(fullWidth && { width: '100%' }),
  containerType: 'inline-size',
  containerName: rootContainerName,
}))

export const SlotsWrapper = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  boxSizing: 'border-box',
  padding: size === 'small' ? theme.spacing(0, 1) : theme.spacing(1),
  width: '100%',
}))

export const LeftSlot = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  maxWidth: '32px',
  margin: size === 'small' ? theme.spacing(0, 1, 0, 0) : theme.spacing(0, 1),
  flex: 0,
}))

export const FlexWrap = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  flexGrow: 1,
  rowGap: theme.spacing(0.5),
  paddingLeft: size === 'small' ? 0 : theme.spacing(1),
  flexDirection: 'row',
  [`@container ${rootContainerName} (max-width: 440px)`]: {
    flexDirection: 'column',
  },
}))

export const ContentSlot = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 0,
  flexGrow: 1,
  paddingRight: theme.spacing(1),
  '& a': {
    ...theme.typography.inherit,
    whiteSpace: 'nowrap',
  },
}))

export const RightSlot = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  flexShrink: 0,
  marginLeft: 'auto',
  gap: theme.spacing(1),
}))
